<template>
  <Teleport to="body">
    <Transition name="lightbox-fade">
      <div v-if="state.isOpen" class="lightbox" role="dialog" aria-modal="true" aria-label="Media viewer" ref="containerRef">
        <div class="lightbox__backdrop" @click="close" aria-hidden="true" />
        <button class="lightbox__close" type="button" aria-label="Close" @click="close" ref="closeButtonRef">
          <IconEmbedded name="remove_2" :size="24" color="rgba(255,255,255,1)" />
        </button>
        <div v-if="state.slides.length > 1" class="lightbox__pagination">{{ state.activeIndex + 1 }} / {{ state.slides.length }}</div>
        <div class="lightbox__content">
          <template v-if="currentSlide">
            <button v-if="state.slides.length > 1" class="lightbox__nav lightbox__nav--prev" type="button" aria-label="Previous" @click="handlePrev">
              <IconEmbedded name="caret-left_2" :size="32" color="rgba(255,255,255,1)" />
            </button>
            <button v-if="state.slides.length > 1" class="lightbox__nav lightbox__nav--next" type="button" aria-label="Next" @click="handleNext">
              <IconEmbedded name="caret-right_2" :size="32" color="rgba(255,255,255,1)" />
            </button>

            <div class="lightbox__media-wrap">
              <Transition :name="slideTransitionName">
                <div :key="`slide-${state.activeIndex}`" class="lightbox__slide" @click.self="close">
                  <img
                    v-if="currentSlide?.type === 'image'"
                    class="lightbox__media lightbox__media--image"
                    :src="currentSlide.src"
                    :alt="currentSlide.caption || 'Image'"
                    :style="imageZoomStyle"
                    draggable="false"
                    @wheel="handleImageWheel"
                    @dragstart.prevent
                    @pointerdown="handleImagePointerDown"
                  />
                  <video
                    v-else-if="currentSlide?.type === 'video'"
                    class="lightbox__media lightbox__media--video"
                    :src="currentSlide.src"
                    :poster="currentSlide.poster"
                    playsinline
                    muted
                    autoplay
                    controls
                    controlsList="nodownload"
                    preload="metadata"
                    ref="videoRef"
                  >
                    <source :src="currentSlide.src" type="video/mp4" />
                    Sorry, your browser does not support embedded videos.
                  </video>
                </div>
              </Transition>
            </div>
            <div v-if="currentSlide.caption" class="lightbox__caption">{{ currentSlide.caption }}</div>
          </template>
        </div>
        <div v-if="state.slides.length > 1" class="lightbox__thumbnails" ref="thumbnailsRef" @wheel="handleThumbnailsWheel">
          <button v-for="(slide, idx) in state.slides" :key="idx" type="button" :class="{ lightbox__thumb: true, 'lightbox__thumb--active': idx === state.activeIndex }" @click="handleThumbClick(idx)">
            <img :src="slide.thumb || slide.poster || slide.src" :alt="slide.caption || ''" draggable="false" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useLightboxState, closeLightbox, goNext, goPrev, goToSlide } from "@logic/lightbox/useLightbox";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

const state = useLightboxState();
const containerRef = ref<HTMLElement | null>(null);
const closeButtonRef = ref<HTMLElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);
const thumbnailsRef = ref<HTMLElement | null>(null);
const slideDirection = ref<"left" | "right" | null>(null);
const imageZoomScale = ref(1);
const imageZoomOrigin = ref("50% 50%");
const imagePanX = ref(0);
const imagePanY = ref(0);
const isImagePanning = ref(false);
const activePanPointerId = ref<number | null>(null);
const panStartPointerX = ref(0);
const panStartPointerY = ref(0);
const panStartOffsetX = ref(0);
const panStartOffsetY = ref(0);
const lastThumbnailWheelAt = ref(0);

const MIN_IMAGE_ZOOM = 1;
const MAX_IMAGE_ZOOM = 3;
const IMAGE_ZOOM_STEP = 0.2;
const THUMBNAIL_WHEEL_COOLDOWN_MS = 180;

const slideTransitionName = computed(() => (slideDirection.value ? `slide-${slideDirection.value}` : "slide-none"));
const imageZoomStyle = computed(() => ({
  transform: imageZoomScale.value > 1 ? `translate(${imagePanX.value}px, ${imagePanY.value}px) scale(${imageZoomScale.value})` : "none",
  transformOrigin: imageZoomOrigin.value,
  cursor: imageZoomScale.value > 1 ? (isImagePanning.value ? "grabbing" : "grab") : "default",
}));

const currentSlide = computed(() => {
  if (!state.value.isOpen || state.value.slides.length === 0) return null;
  return state.value.slides[state.value.activeIndex] ?? null;
});

function close() {
  closeLightbox();
}

function clamp(min: number, value: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function resetImageZoom() {
  stopImagePan();
  imageZoomScale.value = 1;
  imageZoomOrigin.value = "50% 50%";
  imagePanX.value = 0;
  imagePanY.value = 0;
}

function tryAutoPlayVideo() {
  if (currentSlide.value?.type !== "video") return;
  nextTick(() => {
    const video = videoRef.value;
    if (!video) return;
    void video.play().catch(() => undefined);
  });
}

function handleThumbnailsWheel(e: WheelEvent) {
  if (state.value.slides.length <= 1) return;
  e.preventDefault();

  const now = Date.now();
  if (now - lastThumbnailWheelAt.value < THUMBNAIL_WHEEL_COOLDOWN_MS) return;

  const dominantDelta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
  if (Math.abs(dominantDelta) < 2) return;

  lastThumbnailWheelAt.value = now;
  if (dominantDelta > 0) handleNext();
  else handlePrev();
}

function handleImageWheel(e: WheelEvent) {
  if (currentSlide.value?.type !== "image") return;
  e.preventDefault();

  const target = e.currentTarget;
  if (!(target instanceof HTMLElement)) return;

  const rect = target.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return;

  const originX = clamp(0, ((e.clientX - rect.left) / rect.width) * 100, 100);
  const originY = clamp(0, ((e.clientY - rect.top) / rect.height) * 100, 100);
  imageZoomOrigin.value = `${originX}% ${originY}%`;

  const nextScale = clamp(MIN_IMAGE_ZOOM, imageZoomScale.value + (e.deltaY < 0 ? IMAGE_ZOOM_STEP : -IMAGE_ZOOM_STEP), MAX_IMAGE_ZOOM);
  imageZoomScale.value = Number(nextScale.toFixed(2));
  if (imageZoomScale.value === 1) {
    imageZoomOrigin.value = "50% 50%";
    imagePanX.value = 0;
    imagePanY.value = 0;
  }
}

function handleImagePointerDown(e: PointerEvent) {
  if (currentSlide.value?.type !== "image") return;
  if (imageZoomScale.value <= 1) return;
  if (e.button !== 0) return;
  e.preventDefault();

  isImagePanning.value = true;
  activePanPointerId.value = e.pointerId;
  panStartPointerX.value = e.clientX;
  panStartPointerY.value = e.clientY;
  panStartOffsetX.value = imagePanX.value;
  panStartOffsetY.value = imagePanY.value;

  const target = e.currentTarget;
  if (target instanceof HTMLElement && target.setPointerCapture) target.setPointerCapture(e.pointerId);
}

function handleImagePointerMove(e: PointerEvent) {
  if (!isImagePanning.value) return;
  if (activePanPointerId.value !== null && e.pointerId !== activePanPointerId.value) return;
  imagePanX.value = panStartOffsetX.value + (e.clientX - panStartPointerX.value);
  imagePanY.value = panStartOffsetY.value + (e.clientY - panStartPointerY.value);
}

function stopImagePan(e?: PointerEvent) {
  if (e && activePanPointerId.value !== null && e.pointerId !== activePanPointerId.value) return;
  isImagePanning.value = false;
  activePanPointerId.value = null;
}

function handleNext() {
  slideDirection.value = "right";
  goNext();
}

function handlePrev() {
  slideDirection.value = "left";
  goPrev();
}

function handleThumbClick(idx: number) {
  slideDirection.value = null;
  goToSlide(idx);
}

function handleKeydown(e: KeyboardEvent) {
  if (!state.value.isOpen) return;
  if (e.key === "Escape") {
    e.preventDefault();
    close();
  } else if (e.key === "ArrowLeft") {
    e.preventDefault();
    handlePrev();
  } else if (e.key === "ArrowRight") {
    e.preventDefault();
    handleNext();
  }
}

// Focus trap and scroll thumb into view when open
watch(
  () => state.value.isOpen,
  isOpen => {
    if (isOpen) {
      nextTick(() => {
        closeButtonRef.value?.focus();
        const active = thumbnailsRef.value?.querySelector(".lightbox__thumb--active");
        active?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      });
      tryAutoPlayVideo();
    } else {
      resetImageZoom();
    }
  }
);

// Reset video when changing slides
watch(
  () => state.value.activeIndex,
  () => {
    if (videoRef.value) {
      videoRef.value.pause();
      videoRef.value.currentTime = 0;
    }
    tryAutoPlayVideo();
    resetImageZoom();
  }
);

// Scroll active thumbnail into view
watch(
  () => state.value.activeIndex,
  () => {
    nextTick(() => {
      const active = thumbnailsRef.value?.querySelector(".lightbox__thumb--active");
      active?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    });
  }
);

onMounted(() => {
  document.addEventListener("keydown", handleKeydown, true);
  window.addEventListener("pointermove", handleImagePointerMove);
  window.addEventListener("pointerup", stopImagePan);
  window.addEventListener("pointercancel", stopImagePan);
});
onBeforeUnmount(() => {
  document.removeEventListener("keydown", handleKeydown, true);
  window.removeEventListener("pointermove", handleImagePointerMove);
  window.removeEventListener("pointerup", stopImagePan);
  window.removeEventListener("pointercancel", stopImagePan);
  stopImagePan();
});
</script>

<style scoped lang="scss">
.lightbox {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 7px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.92);
}

.lightbox__backdrop {
  position: absolute;
  inset: 0;
  background: transparent;
  cursor: pointer;
}

.lightbox__close {
  position: fixed;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  color: rgba(255, 255, 255, 1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;
  z-index: 10001;

  &:hover {
    opacity: 0.8;
  }
}

.lightbox__pagination {
  position: fixed;
  top: 16px;
  left: 20px;
  height: 36px;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 1);
  font: 17px/1.4 sans-serif;
  z-index: 10001;
}

.lightbox__content {
  position: relative;
  z-index: 1;
  max-width: 100vw;
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-width: 0;
}

.lightbox__nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  padding: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  color: rgba(255, 255, 255, 1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s ease;
  z-index: 10001;

  &:hover {
    opacity: 0.8;
  }

  &--prev {
    left: 16px;
  }
  &--next {
    right: 16px;
  }
}

.lightbox__media-wrap {
  position: relative;
  width: 100vw;
  max-width: 100%;
  max-height: calc(100vh - 180px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.lightbox__slide {
  width: 100%;
  max-height: calc(100vh - 180px);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Slide transitions for arrow nav (left/right keys) */
.slide-right-enter-active,
.slide-right-leave-active,
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.25s ease;
}

.slide-right-enter-from {
  transform: translateX(100vw);
}
.slide-right-leave-to {
  transform: translateX(-100vw);
}
.slide-left-enter-from {
  transform: translateX(-100vw);
}
.slide-left-leave-to {
  transform: translateX(100vw);
}

.slide-right-leave-active,
.slide-left-leave-active,
.slide-none-leave-active {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

/* No animation for thumbnail click */
.slide-none-enter-active,
.slide-none-leave-active {
  transition: opacity 0.15s ease;
}
.slide-none-enter-from,
.slide-none-leave-to {
  opacity: 0;
}

.lightbox__media {
  max-width: 100%;
  max-height: calc(100vh - 180px);
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;

  &--video {
    min-height: 200px;
  }

  &--image {
    transition: transform 0.08s linear;
    will-change: transform;
    user-select: none;
    touch-action: none;
  }
}

.lightbox__caption {
  color: rgba(255, 255, 255, 0.9);
  font: 14px/1.4 sans-serif;
  text-align: center;
  max-width: 90vw;
  margin-top: 12px;
  flex-shrink: 0;
}

.lightbox__thumbnails {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 96px;
  padding: 12px 20px;
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  justify-content: flex-start;
  align-items: center;
  z-index: 10001;
  background: rgba(0, 0, 0, 0.5);

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.lightbox__thumb {
  flex-shrink: 0;
  width: 100px;
  height: 64px;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  overflow: hidden;
  transition:
    border-color 0.15s ease,
    opacity 0.15s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  &:hover {
    opacity: 0.9;
  }

  &--active {
    border-color: rgba(100, 180, 255, 0.9);
  }
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}
.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}
</style>
